// reportLandPropertyPerPage.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";

import dayjs from "dayjs";
import "dayjs/locale/th";
dayjs.locale("th");

// ---------- helpers ----------
const toThaiDate = (d) => {
  if (!d) return "-";
  const x = dayjs(d);
  if (!x.isValid()) return "-";
  return `${x.format("D")} ${x.locale("th").format("MMM")} ${x.year() + 543}`;
};

const fmtMoney = (n) => {
  const v = Number(n ?? 0);
  if (!v) return "-";
  return v.toLocaleString("th-TH", { maximumFractionDigits: 0 }) + " บาท";
};

const fmtArea = (rai, ngan, wa, subwa) => {
  const p = [];
  if (rai || rai === 0) p.push(`${rai ?? 0} ไร่`);
  if (ngan || ngan === 0) p.push(`${ngan ?? 0} งาน`);
  if (wa || wa === 0) p.push(`${wa ?? 0} ตร.วา`);
  if (subwa || subwa === 0) p.push(`${subwa ?? 0} ส่วนวา`);
  return p.join(" ") || "-";
};

const mapMortgage = (t) => {
  if (!t) return "-";
  if (/ขายฝาก/i.test(t)) return "ติดขายฝาก";
  if (/จำนอง|mortgage/i.test(t)) return "ติดจำนอง";
  return "-";
};
const mapSequestrate = (v) => (Number(v) === 1 ? "ติดอายัด" : "-");

const propertyTypeLabel = (id) =>
  id === 1 ? "น.ส.4 จ." : id === 2 ? "น.ส.3 ก." : "-";

const propertyTypeDetailLabel = (id) =>
  id === 1
    ? "ที่ดินว่างเปล่า"
    : id === 2
      ? "ที่ตาบอด"
      : id === 3
        ? "ที่ดินพร้อมสิ่งปลูกสร้าง"
        : id === 4
          ? "ที่นา"
          : id === 5
            ? "ห้องชุด"
            : id === 6
              ? "บ้านเอื้ออาทร"
              : id === 7
                ? "ที่ดินต้องห้าม"
                : id === 8
                  ? "จดรวม 2 โฉนด"
                  : id === 9
                    ? "ที่ถนนทางเข้า"
                    : id === 10
                      ? "ไม่ได้ลงพื้นที่ตรวจสอบ"
                      : "-";

// ---------- วาด 1 หน้า/1 ทรัพย์ ----------
function renderPropertyPage(doc, raw, p, index, total) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = { l: 14, r: 14, t: 14, b: 14 };

  try {
    doc.setFont("THSarabunNew", "bold");
  } catch {}

  // Header
  const debtor =
    [
      raw.CUSTOMER_TNAME || "",
      raw.CUSTOMER_FNAME || "",
      raw.CUSTOMER_LNAME || "",
    ]
      .join(" ")
      .replace(/\s+/g, " ")
      .trim() || "-";

  const assign =
    [raw.LAWYER_NNAME || ""].join(" ").replace(/\s+/g, " ").trim() || "-";

  doc.setFontSize(18);
  doc.text(
    `รายละเอียดข้อมูลที่ดิน แปลงที่ ${index}/${total}`,
    pageW / 2,
    margin.t,
    { align: "center" },
  );
  doc.setFontSize(11);
  doc.text(
    `วันที่พิมพ์: ${toThaiDate(new Date())}`,
    pageW - margin.r,
    margin.t - 2,
    { align: "right" },
  );

  doc.setFontSize(16);
  doc.text(`สัญญา: ${raw.NEW_CONTNO || raw.CONTNO}`, margin.l, margin.t + 15);
  doc.text(`ผู้ทำสัญญา: ${debtor}`, margin.l + 50, margin.t + 15);
  doc.text(`ผู้รับผิดชอบ: ${assign}`, margin.l + 120, margin.t + 15);

  doc.setDrawColor(180);
  doc.line(margin.l, margin.t + 17, pageW - margin.r, margin.t + 17);
  // แทนบล็อก rows เดิมเฉพาะส่วนที่เกี่ยว
  const mortgageLines = [
    mapMortgage(p.mortgage_type),
    p.mortgagee ? `ผู้รับ: ${p.mortgagee}` : null,
    (p.mortgage_balance ?? null) != null
      ? `คงเหลือ: ${fmtMoney(p.mortgage_balance)}`
      : null,
    p.mortgage_start_date || p.mortgage_end_date
      ? `ช่วง: ${toThaiDate(p.mortgage_start_date)} - ${toThaiDate(
          p.mortgage_end_date,
        )}`
      : null,
  ].filter(Boolean);

  const enforceLines = [
    mapSequestrate(p.sequestrate_status),
    p.seize_date ? `ยึดเมื่อ: ${toThaiDate(p.seize_date)}` : null,
    p.legal_execution_office ? `บังคับคดี ${p.legal_execution_office}` : null,
    (p.estimated_enforce_price ?? null) != null
      ? `ราคาประเมิน: ${fmtMoney(p.estimated_enforce_price)}`
      : null,
  ].filter(Boolean);

  const rows = [
    [
      "วันที่สืบทรัพย์",
      toThaiDate(p.investigation_date || raw.investigation_date || raw.DATE),
    ],
    [
      "ตรวจทรัพย์ที่",
      `${(raw.department_of_lands || "-").toString().trim()} ${
        raw.investigation_status === 1
          ? "(เจอทรัพย์)"
          : raw.investigation_status === 2
            ? "(อายัดเงินได้)"
            : "(ไม่เจอทรัพย์)"
      }`.trim(),
    ],
    ["รอบที่สืบ", raw.round_no],
    ["ชื่อเจ้าของ/ผู้ครอบครอง", (p.possessor || "-").toString().trim()],
    ["ประเภททรัพย์", propertyTypeLabel(p.property_type_id)],
    ["ลักษณะที่ดิน", propertyTypeDetailLabel(p.property_detail_id)],
    ["เลขโฉนด/เลขที่ดิน", p.deed_number || "-"],
    [
      "จังหวัด / อำเภอ  / รหัสไปรษณีย์",
      [
        p.district_desc ? `อ.${p.district_desc}` : null,
        p.province_desc ? `จ.${p.province_desc}` : null,
        p.zipcode ? ` ${p.zipcode}` : null,
      ]
        .filter(Boolean)
        .join(" / ") || "-",
    ],
    ["ขนาด (ไร่/งาน/ตร.วา/ส่วนวา)", fmtArea(p.rai, p.ngan, p.wa, p.subwa)],
    ["เลขระวาง/UTM", p.utm || "-"],
    [
      "ตำแหน่ง/หมายเหตุพิกัด",
      p.latitude && p.longitude ? `พิกัด ${p.latitude}, ${p.longitude}` : "-",
    ],
    ["ภาระจำนอง/ขายฝาก", mortgageLines.join("\n") || "-"],
    ["บังคับคดี/ราคาประเมิน", enforceLines.join("\n") || "-"],
    [
      "หมายเหตุ",
      raw.investigation_status === 0
        ? raw.mark || "-"
        : p.mark ||
          raw.investigation_log_list?.[raw.investigation_log_list.length - 1]
            ?.mark ||
          "-",
    ],
  ];

  doc.autoTable({
    startY: margin.t + 20,
    head: [["หัวข้อ", "ข้อมูล"]],
    body: rows,
    styles: {
      font: "THSarabunNew",
      fontSize: 16,
      cellPadding: 2,
      valign: "top",
      fontStyle: "bold",
    },
    headStyles: {
      fillColor: [30, 77, 140],
      textColor: 255,
      fontStyle: "normal",
    },
    columnStyles: {
      columnStyles: {
        0: { cellWidth: 62, fontStyle: "bold", halign: "center" },
        1: { cellWidth: pageW - margin.l - margin.r - 62 }, // ปล่อยซ้ายตามค่าเริ่มต้น
      },
    },
    theme: "grid",
    margin: { left: margin.l, right: margin.r },
    tableWidth: pageW - margin.l - margin.r,
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(10);
      doc.text(
        `หน้า ${data.pageNumber}/${pageCount}`,
        pageW - margin.r,
        pageH - 6,
        { align: "right" },
      );
    },
  });

  // ลายเซ็นผู้บันทึก (พื้นที่ว่าง)
  //   const y = (doc.lastAutoTable?.finalY || margin.t + 14) + 10;
  //   doc.setFontSize(12);
  //   doc.text(
  //     "ผู้บันทึก..............................................................",
  //     pageW - margin.r - 90,
  //     y + 16
  //   );
  //   doc.text(
  //     "(........................................)",
  //     pageW - margin.r - 80,
  //     y + 24
  //   );
}

// ---------- API หลักที่คุณจะเรียก ----------
export function printLandReportPerProperty(raw = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const list = Array.isArray(raw.property_list) ? raw.property_list : [];
  const total = Math.max(list.length, 1);

  if (!list.length) {
    // ไม่มีทรัพย์ -> สร้างหน้าว่างที่ดึงแค่ข้อมูลสัญญา/ลูกหนี้เท่าที่มี
    renderPropertyPage(doc, raw, {}, 1, 1);
  } else {
    list.forEach((p, i) => {
      if (i > 0) doc.addPage();
      renderPropertyPage(doc, raw, p, i + 1, total);
    });
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const w = window.open(url);
  if (!w) doc.save(`land_property_${dayjs().format("YYYYMMDD")}.pdf`);
}
